import Formsy from "formsy-react";
import * as React from "react";
import { isIOS, isMobile } from "react-device-detect";
import {
  AuthenticationClient,
  CollectStatus,
  StatusResponse,
} from "../api/ApiClient";
import Spinner from "../components/Spinner";
import { UserContext } from "../context/UserContext";
import { parseQueryString } from "../services/QueryString";

interface Props {
  signedOut: boolean;
  onCompleted: (response: StatusResponse) => void;
}
interface State {
  valid: boolean;
  message: string;
  posting: boolean;
  autoLaunch: boolean;
  link?: string;
  orderRef: string;
  qrCodeBase64?: string;
  qrState?: string;
  collectStatus?: CollectStatus;
}

export class SignIn extends React.Component<Props, State> {
  private readonly orderRefSessionStorageKey = "rebel.orderRefbankid.";
  public readonly state: Readonly<State> = {
    valid: false,
    message: null,
    posting: !!sessionStorage.getItem(this.orderRefSessionStorageKey),
    autoLaunch: isMobile,
    orderRef: null,
  };

  private errorCount = 0;

  private formRef: React.RefObject<any> = React.createRef();
  private readonly authenticationClient: AuthenticationClient =
    new AuthenticationClient();

  static contextType = UserContext;
  context!: React.ContextType<typeof UserContext>;

  private posting: boolean = !!sessionStorage.getItem(
    this.orderRefSessionStorageKey
  );

  componentDidMount() {
    let params = parseQueryString(window.location.search);

    let orderRef =
      (params && params.get("orderRef")) ||
      sessionStorage.getItem(this.orderRefSessionStorageKey);

    if (orderRef) {
      console.debug("Found orderRef on mount", orderRef);
      this.posting = true;
      this.setState({ posting: true, orderRef });
      this.checkStatus(orderRef);
      sessionStorage.removeItem(this.orderRefSessionStorageKey);
    }
  }

  render() {
    return (
      <Formsy
        ref={this.formRef}
        formNoValidate
        noValidate={true}
        onSubmit={this.initiate}
        onValid={this.onValidForm}
        onInvalid={this.onInvalidForm}>
        {isMobile && this.state.autoLaunch && (
          <div>
            <div className="form-group">
              <label className="control-label">
                Logga in med Mobilt BankID på den här enheten
              </label>
              {!this.state.posting ? (
                <button
                  className="btn btn-submit btn-lg mt-0"
                  type="submit"
                  disabled={this.state.posting}>
                  {this.state.posting ? "Loggar in..." : "Logga in"}
                </button>
              ) : (
                <Spinner />
              )}
              {this.state.link && (
                <>
                  <a href={this.state.link} className="btn btn-submit btn-lg">
                    Öppna BankID
                  </a>
                </>
              )}
              {this.state.message ? <p>{this.state.message}</p> : null}
            </div>
            {!this.state.posting && (
              <a href="#" onClick={this.toogleAutoLaunch} className="mt-2">
                Logga in från en annan enhet
              </a>
            )}
          </div>
        )}
        {!this.state.autoLaunch && (
          <div>
            <div className="form-group">
              <label className="control-label">
                Logga in med Mobilt BankID
              </label>
              {!this.state.posting ? (
                <>
                  {this.state.qrCodeBase64 ? (
                    <img
                      className="w-50"
                      src={"data:image/png;base64, " + this.state.qrCodeBase64}
                    />
                  ) : null}
                </>
              ) : (
                <Spinner />
              )}
              {this.state.message ? <p>{this.state.message}</p> : null}
              {!this.state.posting && !this.state.qrCodeBase64 ? (
                <button
                  className="btn btn-submit btn-lg mt-0"
                  type="submit"
                  disabled={this.state.posting}>
                  {this.state.collectStatus == CollectStatus.Failed
                    ? "Försök igen"
                    : "Starta inloggning"}
                </button>
              ) : null}
            </div>
            {isMobile && !this.state.posting && (
              <a href="#" onClick={this.toogleAutoLaunch} className="mt-2">
                Logga in med Mobilt BankID på den här enheten
              </a>
            )}
          </div>
        )}
      </Formsy>
    );
  }

  onValidForm = () => {
    this.setState({
      valid: true,
    });
  };

  onInvalidForm = () => {
    this.setState({
      valid: false,
    });
  };

  toogleAutoLaunch = () => {
    this.setState({ autoLaunch: !this.state.autoLaunch, message: "" });
  };

  initiate = async () => {
    if (this.posting) {
      return;
    }
    this.posting = true;
    this.errorCount = 0;

    this.setState({
      posting: true,
      message: "Startar BankId-inloggning",
      link: null,
      collectStatus: CollectStatus.Pending,
    });
    try {
      let response = await this.authenticationClient.initiate();
      console.debug("Got initiated response", response, this.state.autoLaunch);
      if (this.state.autoLaunch) {
        let link =
          (isIOS ? "https://app.bankid.com/" : "bankid:///") +
          "?autostarttoken=" +
          response.autoStartToken +
          "&redirect=null";

        if (isIOS) {
          sessionStorage.setItem(
            this.orderRefSessionStorageKey,
            response.orderRef
          );
          this.setState({ link });
        } else {
          window.location.href = link;
        }
      } else {
        console.debug("Stored qr state", {
          qrCodeBase64: response.qrCodeBase64Encoded,
          qrState: response.state,
        });
        this.setState({
          qrCodeBase64: response.qrCodeBase64Encoded,
          qrState: response.state,
          message: "Starta BankID-appen",
          posting: false,
        });
      }

      setTimeout(() => {
        this.checkStatus(response.orderRef);
      }, 500);
    } catch (error) {
      console.error("Signin initiation failed", error);
      this.posting = false;
      this.setState({
        posting: false,
        qrCodeBase64: null,
        message: "Någonting oväntat hände, var vänlig försök igen.",
        link: null,
      });
    }
  };

  checkStatus = async (orderRef: string) => {
    try {
      let response = await this.authenticationClient.status(
        orderRef,
        this.state.autoLaunch ? "" : this.state.qrState
      );
      sessionStorage.removeItem(this.orderRefSessionStorageKey);
      console.debug("Got login status response", response, orderRef);

      this.setState({ collectStatus: response.status });
      switch (response.status) {
        case CollectStatus.Pending: {
          this.setState({
            message: response.message,
            qrCodeBase64: response.qrCodeBase64Encoded,
          });
          setTimeout(() => {
            this.checkStatus(orderRef);
          }, 2000);
          break;
        }
        case CollectStatus.Failed: {
          this.posting = false;
          this.setState({
            message: response.message,
            posting: false,
            qrCodeBase64: null,
            link: null,
          });
          break;
        }
        case CollectStatus.Complete: {
          this.props.onCompleted(response);
          break;
        }
        default: {
          this.posting = false;
          this.setState({
            message:
              response.message ||
              "Någonting oväntat hände, var vänlig försök igen.",
            posting: false,
            qrCodeBase64: null,
            link: null,
          });
        }
      }
    } catch (error) {
      if (this.errorCount > 1) {
        sessionStorage.removeItem(this.orderRefSessionStorageKey);
        console.error("Signin status failed", error);
        this.posting = false;
        this.setState({
          message: "Någonting oväntat hände, var vänlig försök igen",
          posting: false,
          qrCodeBase64: null,
          link: null,
        });
      } else {
        this.errorCount++;
        console.debug("Got error check status", error, this.errorCount);
        setTimeout(() => {
          this.checkStatus(orderRef);
        }, 2000);
      }
    }
  };
}
